import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import IrisPage from 'yoda-site-components/lib/components/IrisPage/IrisPage';
import ScrollTop from 'yoda-site-components/lib/components/ScrollTop/ScrollTop';
import { hotdealsPostProcessor } from 'yoda-interfaces/lib/IrisPage/IrisPage';
import irisComponentsForDept from 'yoda-site-components/lib/iris/components/forDepartment';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import { selectIrisData } from 'yoda-site-components/lib/selectors/IrisPageSelector';
import {
    selectContext,
    selectDeviceName,
    isPreview as selectIsPreview,
    selectIrisFallbackPreferences,
    selectFeatureFlags,
    selectSeoProdHostName,
    selectRequestUrl,
} from 'yoda-site-components/lib/selectors/ContextSelector';
import SeoSchema from 'yoda-site-components/lib/components/SeoSchema/SeoSchema';
import { onPageReady } from '../../../actions/DepartmentAction';
import renderMiniPDP from '../../../helper/MiniPDPHelper';
import flexTwoColsZoneOverride from '../../../iris/zoneOverrides/flexTwoCols';

const defaultPreferences = {
    renderingStrategy: {
        dept: {
            limitInitialComponentsRendered: {
                mobile: 8,
                desktop: 9,
                tablet: 8,
            },
            offset: {
                mobile: 500,
                desktop: 800,
                tablet: 500,
            },
        },
    },
};

/**
 * Derive the current department's id from the iris breadcrumb data.
 * This is the same source Adobe uses (breadcrumbsData[].deptId). Used to scope
 * the Constructor rec pod to this department via the `groupId` prop.
 * Always returns a string (empty string when no deptId is present).
 */
export const getDepartmentGroupId = (irisData) => {
    const breadcrumbs = _get(irisData, 'taxonomyData.breadcrumbsData', []);
    const crumb = [...breadcrumbs].reverse().find((item) => item && item.deptId);
    return _get(crumb, 'deptId', '');
};
// eslint-disable-next-line react/no-redundant-should-component-update
export class DepartmentPage extends PureComponent {
    static defaultProps = {
        actions: {},
        irisData: {},
        params: {},
        location: {},
        isPreview: false,
        offset: defaultPreferences.renderingStrategy.dept.offset,
        limitInitialComponentsRendered:
            defaultPreferences.renderingStrategy.dept.limitInitialComponentsRendered,
        deviceName: 'mobile',
        seoProdHostName: 'https://www.jcpenney.com',
        enableSeoDepartmentSchema: true,
        irisFallbackEnabled: false,
        requestUrl: '',
        history: {},
    };

    static propTypes = {
        actions: PropTypes.objectOf(PropTypes.func),
        irisData: PropTypes.objectOf(
            PropTypes.oneOfType([
                PropTypes.object,
                PropTypes.string,
                PropTypes.number,
                PropTypes.bool,
            ])
        ),
        params: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.object, PropTypes.string])),
        location: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.object, PropTypes.string])),
        isPreview: PropTypes.bool,
        // when using react rendering strategies use this props to pass height and offset to ref div
        offset: PropTypes.number,
        deviceName: PropTypes.string,
        limitInitialComponentsRendered: PropTypes.string,
        seoProdHostName: PropTypes.string,
        enableSeoDepartmentSchema: PropTypes.bool,
        irisFallbackEnabled: PropTypes.bool,
        enableMiniPDPFeature: PropTypes.bool.isRequired,
        requestUrl: PropTypes.string,
        history: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.departmentNavHeight = null;
        this.departmentS2Height = null;
        this.state = {
            departmentLowerZoneSm: true,
        };
    }

    componentDidMount() {
        const { irisData, actions } = this.props;

        /* istanbul ignore next */
        if (!_isEmpty(irisData)) {
            const { analytics } = irisData;
            if (analytics) {
                const { pageType, pageName } = analytics;
                actions?.onPageReady(pageName, pageType);
            }
        }

        LocalStorage.removeData('scrollToCartridge');
        LocalStorage.removeData('galleryScrollTo');

        /* check for height changes every 1 sec for 10 sec */
        const checkHeightInterval = setInterval(this.handleCheckHeightChange, 1000);
        setTimeout(() => clearInterval(checkHeightInterval), 10000);
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (nextState?.departmentLowerZoneSm !== this?.state?.departmentLowerZoneSm) {
            return true;
        }
        if (nextProps.irisData?.id && nextProps.irisData.id !== this.props.irisData?.id) {
            return true;
        }
        return false;
    }

    componentDidUpdate(prevProps) {
        if (prevProps.irisData?.id !== this.props.irisData?.id) {
            const { analytics } = this.props.irisData;
            if (analytics) {
                const { pageType, pageName } = analytics;
                this.props.actions.onPageReady(pageName, pageType);
            }
        }
    }

    handleDepartmentPage2ColCallback = (element) => {
        const id = element?.dataset?.automationId;
        if (id === 'zone-Navigation' || id === 'zone-LHN') this.departmentNavHeight = element;
        if (id === 'zone-S2') this.departmentS2Height = element;

        this.handleCheckHeightChange();
    };

    handleCheckHeightChange = () => {
        // if height is within 50px difference, go ahead and make main content full width
        const navHeight = this?.departmentNavHeight?.clientHeight || 0;
        const s2Height = this?.departmentS2Height?.clientHeight || 0;
        if (navHeight > s2Height + 50) {
            this.setState({
                departmentLowerZoneSm: true,
            });
        } else {
            this.setState({
                departmentLowerZoneSm: false,
            });
        }
    };

    transformBreadCrumbsData = (breadcrumbs, seoUrl) => {
        let breadCrumbsArr = [];
        const { seoProdHostName } = this.props;

        /* istanbul ignore else */
        if (breadcrumbs?.length) {
            breadCrumbsArr = breadcrumbs.map((val, index) => {
                let breadCrumbLabel = '';
                let pageUrl = '';
                let itemURL = '';

                // iris data
                breadCrumbLabel = val?.linkText || '';
                pageUrl = val?.link?.url || '';

                if (pageUrl === '/') {
                    itemURL = seoProdHostName;
                } else if (_isEmpty(pageUrl)) {
                    itemURL = `${seoProdHostName}${seoUrl}`;
                } else {
                    itemURL = `${seoProdHostName}${pageUrl}`;
                }

                const breadcrumbsObj = {
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                        '@id': itemURL,
                        '@type': 'Thing',
                        name: breadCrumbLabel,
                    },
                };
                return breadcrumbsObj;
            });
        }
        return breadCrumbsArr;
    };

    buildSchema = () => {
        const { seoProdHostName, irisData } = this.props;
        let breadcrumbs = [];
        let seoUrl = '';
        let seoPageTitle = '';
        let breadCrumbsTransformed = [];

        // Iris Data scheme
        breadcrumbs = _get(irisData, 'taxonomyData.breadcrumbsData', []);
        seoUrl = _get(irisData, 'taxonomyData.seoUrl', '');
        seoPageTitle = _get(irisData, 'seoMetaData.content.pageTitle', '');
        breadCrumbsTransformed = this.transformBreadCrumbsData(breadcrumbs, seoUrl);

        const isInHeadTag = true;
        const schema = {
            '@context': 'http://schema.org',
            '@type': 'WebPage',
            name: seoPageTitle,
            url: `${seoProdHostName}${seoUrl}`,
            breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: breadCrumbsTransformed,
            },
        };
        return <SeoSchema isInHeadTag={isInHeadTag} schema={schema} />;
    };

    render() {
        const patt = new RegExp('/d/'); // Regex to check the URL
        const {
            location: { pathname = '', search = '' },
            limitInitialComponentsRendered,
            offset,
            deviceName,
            irisData,
            enableSeoDepartmentSchema,
            isPreview,
            irisFallbackEnabled,
            params,
            enableMiniPDPFeature,
        } = this.props; // getting location from router
        const { departmentLowerZoneSm } = this.state;
        let formattedUrl;
        /* istanbul ignore next */
        if (isPreview) {
            formattedUrl = `${pathname}${search}`;
        } else if (patt.test(pathname)) {
            formattedUrl = `${pathname}`;
        } else {
            formattedUrl = `/g/${params?.department}/${params?.ntype}?pageType=X2H2`;
        }

        const renderMiniPDPComponent = enableMiniPDPFeature ? renderMiniPDP() : null;

        const irisPageProps = {
            params: formattedUrl,
            irisFallbackEnabled,
            irisComponents: irisComponentsForDept,
            'data-automation-id': 'department',
            postProcessor: hotdealsPostProcessor,
            limitInitialComponentsRendered: limitInitialComponentsRendered[deviceName],
            offset: offset[deviceName],
            zoneRenderer: [
                {
                    zone: 'flex-two-cols',
                    renderer: flexTwoColsZoneOverride,
                },
            ],
        };

        // Department id from breadcrumbs, forwarded to the recommendations slot
        // as the Constructor `groupId`. Only included when non-empty so the
        // slot's configured groupId still wins otherwise. IrisPage passes this
        // through its propsData to the rendered components.
        const departmentGroupId = getDepartmentGroupId(irisData);

        const commonProps = {
            handleDepartmentPage2ColCallback: this.handleDepartmentPage2ColCallback,
            departmentLowerZoneSm,
            pageType: 'DEPARTMENT',
            aggregateAPI: true,
            ...(departmentGroupId ? { groupId: departmentGroupId } : {}),
        };

        return (
            <>
                <ScrollTop />
                {!_isEmpty(irisData) && enableSeoDepartmentSchema && this.buildSchema()}

                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <IrisPage {...irisPageProps} {...commonProps} />
                {renderMiniPDPComponent}
            </>
        );
    }
}

const mapStateToProps = (state) => {
    const context = selectContext(state);
    const featureFlags = selectFeatureFlags(state);
    return {
        irisData: selectIrisData(state),
        isPreview: selectIsPreview(state),
        cmFallbackPreferences: selectIrisFallbackPreferences(state),
        seoProdHostName: selectSeoProdHostName(state),
        requestUrl: selectRequestUrl(state),
        irisFallbackEnabled: _get(featureFlags, 'enableIrisFallback', true),
        disableIrisCall: _get(featureFlags, 'disableIrisCall', false),
        enableSeoDepartmentSchema: _get(featureFlags, 'enableSeoDepartmentSchema', true),
        enableMiniPDPFeature: _get(featureFlags, 'enableMiniPDPFeature', true),
        deviceName: selectDeviceName({ context }),
        limitInitialComponentsRendered: _get(
            context,
            'preferences.renderingStrategy.home.limitInitialComponentsRendered',
            defaultPreferences.renderingStrategy.dept.limitInitialComponentsRendered
        ),
        offset: _get(
            context,
            'preferences.renderingStrategy.home.offset',
            defaultPreferences.renderingStrategy.dept.offset
        ),
    };
};

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(
        {
            onPageReady,
        },
        dispatch
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(DepartmentPage);
